import type { ReportAggregation } from "@glorify-llc/accounting-core";

export interface MinistryGrowthRow {
  ministry_id: string;
  ministry_name: string;
  current_members: number;
  new_members_period: number;
  departed_members_period: number;
  net_change: number;
  growth_rate_percent: number;
}

export type MinistryGrowthContext = {
  asOf: Date;
  ministries: MinistryGrowthRow[];
};

/**
 * Coerce a possibly-null/undefined/NaN/Infinity/string-numeric value to a
 * finite number. PostgREST serialises NUMERIC columns as strings, and the
 * underlying view types every column as nullable; without coercion a single
 * unparseable upstream value poisons aggregates (NaN propagates through `+`)
 * and `toFixed` returns "NaN" on non-finite inputs (rendering "+NaN%").
 *
 * Infinity is also coerced to 0 — the view emits Infinity when prior-period
 * member count is 0 (`(current - 0) / 0`), and that should not bleed into
 * the displayed growth-rate label.
 */
function toFiniteNumber(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Format a finite-number percent value with a sign prefix and "%" suffix. */
function formatGrowthPercent(value: number): string {
  const safe = toFiniteNumber(value);
  // toFixed handles "-" automatically; we only need to add an explicit "+"
  // for non-negative values so callers can see direction at a glance.
  const sign = safe >= 0 ? "+" : "";
  return `${sign}${safe.toFixed(1)}%`;
}

export function buildMinistryGrowthAggregation(context: MinistryGrowthContext): ReportAggregation {
  const ministries = Array.isArray(context.ministries) ? context.ministries : [];

  const reportRows = ministries.map((m) => {
    const id = typeof m.ministry_id === "string" && m.ministry_id.length > 0
      ? m.ministry_id
      : "unknown";
    const rawName = typeof m.ministry_name === "string" ? m.ministry_name.trim() : "";
    const currentMembers = toFiniteNumber(m.current_members);
    const newMembers = toFiniteNumber(m.new_members_period);
    const departedMembers = toFiniteNumber(m.departed_members_period);
    const netChange = toFiniteNumber(m.net_change);
    const growthRatePercent = toFiniteNumber(m.growth_rate_percent);

    return {
      id,
      label: rawName.length > 0 ? rawName : "Unknown Ministry",
      entityType: "ministry" as const,
      drilldownUrl: `/ministries/${encodeURIComponent(id)}`,
      currentMembers,
      newMembers,
      departedMembers,
      netChange,
      growthRate: formatGrowthPercent(growthRatePercent),
      growthRatePercent,
    };
  });

  // Stable, locale-independent sort: highest growth-rate first so leaders
  // surface at the top of the list and the chart's `maxItems: 10` slice
  // captures the strongest movers. Tiebreak by ministry name then id so
  // identical rates and names still produce a deterministic order across
  // reloads. Use bare comparison operators (not localeCompare) — locale-
  // dependent ordering causes non-deterministic snapshots across runtimes.
  reportRows.sort((a, b) => {
    const ar = a.growthRatePercent;
    const br = b.growthRatePercent;
    if (ar !== br) return br - ar;
    if (a.label < b.label) return -1;
    if (a.label > b.label) return 1;
    if (a.id < b.id) return -1;
    if (a.id > b.id) return 1;
    return 0;
  });

  // Aggregate over coerced values so a single non-numeric row cannot poison
  // the summary totals via NaN propagation.
  let totalMembers = 0;
  let totalNew = 0;
  let totalDeparted = 0;
  for (const m of ministries) {
    totalMembers += toFiniteNumber(m.current_members);
    totalNew += toFiniteNumber(m.new_members_period);
    totalDeparted += toFiniteNumber(m.departed_members_period);
  }

  return {
    asOf: context.asOf,
    summary: [
      { label: "Ministries", value: ministries.length },
      { label: "Total Members", value: totalMembers },
      { label: "New Members", value: totalNew },
      { label: "Departed", value: totalDeparted },
    ],
    rows: reportRows,
  };
}
